import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import BackButton from '../../components/BackButton';
import { fetchUserCampaignContestByID, resignFromCampaignContest } from '../../api/campaigns';
import { fetchUserTeams } from '../../api/userTeams';
import { startGame, startGameWithSubs } from '../../api/games';
import { toast } from 'react-toastify';
import { filterSelectableUserTeams } from '../../helpers/teamHelpers';
import FatiguedSubstitutionModal from '../../components/FatiguedSubstitutionModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import RewardDisplay from '../../components/RewardDisplay';
import ConstraintTags from '../../components/ConstraintTags';
import DifficultyBadge from '../../components/DifficultyBadge';
import { checkTeamCompliance } from '../../api/constraints';
import { handleActiveGameExists } from '../../helpers/startGameConflicts';

const PlayCampaignContestPage = () => {
    const { userCampaignContestID } = useParams();
    const navigate = useNavigate();
    const [currentUser] = useAtom(currentUserAtom);
    const [campaignContest, setCampaignContest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [userTeams, setUserTeams] = useState([]);
    const [selectedUserTeam, setSelectedUserTeam] = useState('');

    const [teamCompliance, setTeamCompliance] = useState(null);

    // Fatigue substitution state (409 modal flow)
    const [showSubModal, setShowSubModal] = useState(false);
    const [fatiguedData, setFatiguedData] = useState(null);
    const [pendingPayload, setPendingPayload] = useState(null);


    useEffect(() => {
        const fetchContestDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                const contestData = await fetchUserCampaignContestByID(userCampaignContestID);
                setCampaignContest(contestData);
            } catch (err) {
                setError('Error fetching campaign contest');
                toast.error('Error fetching campaign contest: ' + err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchContestDetails();
    }, [userCampaignContestID]);

    useEffect(() => {
        const fetchTeams = async () => {
            try {
                const teamsData = await fetchUserTeams();
                setUserTeams(filterSelectableUserTeams(teamsData));
            } catch (err) {
                toast.error('Error fetching user teams: ' + err.message);
            }
        };

        fetchTeams();
    }, []);

    useEffect(() => {
        const constraints = campaignContest?.CampaignContest?.constraints;
        if (constraints?.length > 0) {
            checkTeamCompliance(constraints)
                .then(data => setTeamCompliance(data.teams))
                .catch(err => console.error('Failed to check team compliance:', err));
        }
    }, [campaignContest]);

    const isTeamCompliant = (teamID) => {
        if (!teamCompliance) return true;
        const entry = teamCompliance.find(t => String(t.userTeamID) === String(teamID));
        return entry?.isCompliant ?? true;
    };

    const getTeamViolation = (teamID) => {
        if (!teamCompliance) return null;
        const entry = teamCompliance.find(t => String(t.userTeamID) === String(teamID));
        return entry?.violations?.[0] ?? null;
    };

    const handleTeamChange = e => {
        setSelectedUserTeam(e.target.value);
    };


    const buildPayload = () => ({
        userCampaignContestID,
        homeUserTeamID: selectedUserTeam,
        visitingUserTeamID: campaignContest.CampaignContest.userTeamID,
    });

    const handlePlayNextGame = async () => {
        if (!selectedUserTeam) {
            toast.error('Please select a team');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const newGame = await startGame(buildPayload());
            navigate(`/game/${newGame.gameID}/play`);
        } catch (err) {
            if (handleActiveGameExists(err, navigate)) return;
            if (err.response?.status === 409 && err.response?.data?.code === 'PLAYERS_FATIGUED') {
                setFatiguedData({
                    unavailableCards: err.response.data.unavailableCards,
                    availableSubstitutes: err.response.data.availableSubstitutes,
                });
                setPendingPayload(err.response.data.pendingPayload);
                setShowSubModal(true);
            } else {
                const msg = err.response?.data?.error || err.message;
                setError(msg);
                toast.error(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubConfirm = useCallback(async subs => {
        setShowSubModal(false);
        setLoading(true);
        setError(null);

        try {
            const newGame = await startGameWithSubs({ ...pendingPayload, substitutions: subs });
            toast.success('Game created — tired players were substituted automatically.');
            navigate(`/game/${newGame.gameID}/play`);
        } catch (err) {
            const msg = err.response?.data?.error || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [pendingPayload, navigate]);

    const handleAllHealed = useCallback(async () => {
        setShowSubModal(false);
        setFatiguedData(null);
        setLoading(true);
        setError(null);
        try {
            const newGame = await startGame(pendingPayload);
            navigate(`/game/${newGame.gameID}/play`);
        } catch (err) {
            if (handleActiveGameExists(err, navigate)) return;
            toast.error(err.response?.data?.error || 'Failed to start game');
        } finally {
            setLoading(false);
            setPendingPayload(null);
        }
    }, [pendingPayload, navigate]);

    const handleSubCancel = useCallback(() => {
        setShowSubModal(false);
        setFatiguedData(null);
        setPendingPayload(null);
    }, []);

    const handleResignFromSeries = async () => {
        const confirmResignation = window.confirm('Are you sure you want to resign from this series?');
        if (!confirmResignation) return;

        try {
            await resignFromCampaignContest(userCampaignContestID);
            toast.success('You have resigned from the campaign contest.');
            navigate(`/campaigns/${campaignContest.CampaignContest.campaignID}`);
        } catch (err) {
            toast.error('Error resigning from the campaign contest: ' + err.message);
        }
    };

    if (loading && !campaignContest) {
        return <LoadingSpinner />;
    }

    if (error && !campaignContest) {
        return <p className="error">{error}</p>;
    }

    if (!campaignContest) {
        return <p>No campaign contest found.</p>;
    }

    const { Games, CampaignContest } = campaignContest;
    const totalGames = CampaignContest.numOfGames;
    const hasConstraints = CampaignContest.constraints?.length > 0;
    const noCompliantTeams = hasConstraints && teamCompliance && teamCompliance.every(t => !t.isCompliant);

    // Sort games oldest first (by created date)
    const sortedGames = [...Games].sort((a, b) => new Date(a.created) - new Date(b.created));

    // Check if there's a game in progress (not final, no result)
    const gameInProgress = sortedGames.find(g => !g.isFinal && !g.resultDescription);

    const gamePlaceholders = Array.from({ length: totalGames }, (_, index) => {
        const game = sortedGames[index];
        if (game) {
            return (
                <tr key={game.gameID}>
                    <td>{index + 1}</td>
                    <td>{new Date(game.created).toLocaleDateString()}</td>
                    <td>
                        {game.resultDescription ? (
                            <Link to={`/game/${game.gameID}/summary`} className="button">{game.resultDescription}</Link>
                        ) : (
                            <Link to={`/game/${game.gameID}/play`} className="button">Continue</Link>
                        )}
                    </td>
                </tr>
            );
        } else {
            return (
                <tr key={index}>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                </tr>
            );
        }
    });

    return (
        <div className="play-campaign-contest-page">
            <div className="contest-header-row">
                <div className="contest-header-card">
                    <div className="banner-header">
                        <BackButton to={`/campaigns/${CampaignContest.campaignID}`} />
                        <h2>{CampaignContest.name}</h2>
                        {currentUser?.isAdmin && (
                            <button className="btn-secondary" onClick={() => navigate(`/campaign-contests/${CampaignContest.campaignContestID}/edit`)}>Edit</button>
                        )}
                    </div>
                    {CampaignContest.desc && <p className="contest-desc">{CampaignContest.desc}</p>}
                    <DifficultyBadge difficulty={CampaignContest.difficulty} />
                    {CampaignContest.constraints?.length > 0 && (
                        <ConstraintTags constraints={CampaignContest.constraints} />
                    )}
                    <div className="series-score">
                        <span className="score-label">Series</span>
                        <span className="score-value">{campaignContest.wins} - {campaignContest.losses}</span>
                    </div>
                    {campaignContest.resultDescription && (
                        <div className="contest-result">{campaignContest.result}</div>
                    )}
                </div>
                <RewardDisplay reward={CampaignContest.Reward} isClaimed={campaignContest.isMaxRewardsCapped} />
            </div>

            <div className="games-section">
                <h3>Games</h3>
                <div className="table-scroll-wrapper">
                    <table className="series-table">
                        <thead>
                            <tr>
                                <th>Game</th>
                                <th>Date</th>
                                <th>Result</th>
                            </tr>
                        </thead>
                        <tbody>{gamePlaceholders}</tbody>
                    </table>
                </div>
            </div>

            {gameInProgress ? (
                <div className="play-next-section">
                    <Link to={`/game/${gameInProgress.gameID}/play`} className="btn-primary btn-block">
                        Continue Game in Progress
                    </Link>
                </div>
            ) : Games.length < totalGames ? (
                <div className="play-next-section">
                    <div className="form-group">
                        <label>Select Your Team</label>
                        <select value={selectedUserTeam} onChange={handleTeamChange}>
                            <option value="">Select your team</option>
                            {userTeams.map(team => {
                                const compliant = isTeamCompliant(team.userTeamID);
                                const violation = getTeamViolation(team.userTeamID);
                                return (
                                    <option
                                        key={team.userTeamID}
                                        value={team.userTeamID}
                                        disabled={!compliant}
                                    >
                                        {team.name} ({team.score || 0}){!compliant ? ` — ${violation || 'Does not meet constraints'}` : ''}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                    {hasConstraints && (
                        <Link
                            to={`/team/create?constraints=${encodeURIComponent(JSON.stringify(CampaignContest.constraints))}`}
                            className={`build-compliant-team-link${noCompliantTeams ? ' build-compliant-team-link--urgent' : ''}`}
                        >
                            <span className="build-compliant-team-link__icon">🔧</span>
                            <span className="build-compliant-team-link__text">
                                {noCompliantTeams
                                    ? 'None of your teams qualify — build one that does'
                                    : 'Build a team for this challenge'}
                            </span>
                        </Link>
                    )}
                    <button className="btn-primary" onClick={handlePlayNextGame} disabled={loading}>
                        {loading ? 'Creating...' : 'Play Next Game'}
                    </button>
                </div>
            ) : (
                <p>All games in the series have been played.</p>
            )}

            <div className="contest-footer-links">
                <span onClick={handleResignFromSeries} className="resign-link">Resign from Series</span>
            </div>

            {showSubModal && fatiguedData && (
                <FatiguedSubstitutionModal
                    unavailableCards={fatiguedData.unavailableCards}
                    availableSubstitutes={fatiguedData.availableSubstitutes}
                    onConfirm={handleSubConfirm}
                    onCancel={handleSubCancel}
                    onAllHealed={handleAllHealed}
                    title="Some Players Need Rest"
                />
            )}
        </div>
    );
};

export default PlayCampaignContestPage;
